<template>
    <div class="locations-pickup-points">
      <div class="public-container public-container-mobile">
        <h3 class="vz-text-align-left">
          {{ title }}
          <div class="color-primary flex flex-align-items-center mt-15">
            <vz-icon
              size=36
              variant="primary"
              url="/svg/vz-coins.svg"
              class="mr-10"
            />
            Стоимость: {{ pickupPointCost }}
          </div>
        </h3>
      </div>
      <div class="locations-pickup-points-map mt-30">
        <div class="public-container public-container-mobile">
          <div class="locations-pickup-points-map-card">
            <div class="locations-pickup-points-map-card-title">
              Выбор пункта выдачи
            </div>
            <vz-direction
              ref="direction"
              hide-switcher
              direction="pickupPoint"
              v-model:location-guid="localLocationGuid"
              v-model:pickup-point-guid="localPickupPointGuid"
              @update:pickup-point-guid="setPickupPoint($event, true)"
              @update:location-guid="onLocationChange"
            />
            <hr class="my-30" />
            <vz-button
              big
              :disabled="!localPickupPointGuid.length"
              variant="primary"
              @click="router.push(orderRoute)"
            >
              Выбрать
            </vz-button>
          </div>
        </div>
        <vz-yandex-map
          has-tooltip
          v-model="map"
          v-model:location="locationValue"
          :controls="['zoomControl']"
          :has-search="false"
        >
          <yandex-map-listener :settings="{ onUpdate: onMapUpdate }" />
          <vz-yandex-map-default-clusterer
            :markers="pickupPointsMarkers"
            @marker-click="setPickupPoint($event.guid, true)"
          />
        </vz-yandex-map>
      </div>
    </div>
</template>

<script setup lang="ts">
import {PropType} from 'vue'
import {storeToRefs} from 'pinia'
import {useCityStore} from '~/stores/city'
import {RouteLocationRaw} from 'vue-router';
import {getPickupPoint, getPickupPointByBounds} from '~/api/order';
import VzYandexMap from '~/components/ui-kit/map/vz-yandex-map.vue';
import {asyncComputed, useDebounceFn, whenever} from '@vueuse/core';
import type {LngLat, LngLatBounds, YMap, YMapLocationRequest} from '@yandex/ymaps3-types';
import {PickupPoint, Location, LocationRegion} from '~/types/types';
import {deepCopyObject} from '~/common/functions';
import type {MapEventUpdateHandler} from '@yandex/ymaps3-types/imperative/YMapListener';
import {YandexMapListener} from 'vue-yandex-maps';
import VzYandexMapDefaultClusterer from '~/components/ui-kit/map/vz-yandex-map-default-clusterer.vue';
import VzDirection from '~/components/personal/order/direction/vz-direction.vue'

const props = defineProps({
  region: {
    type: Object as PropType<LocationRegion>,
    default: () => {}
  },
})

const { from } = storeToRefs(useCityStore())

const router = useRouter()

const defaultCoordinates = Array.isArray(from.value.coordinates)
  ? from.value.coordinates
  : from.value?.coordinates?.length
    ? from.value?.coordinates?.split(',')
    : [37.617815, 55.755773]
const defaultZoom = 10
const pickupPointCost = '720 ₽'

const map = shallowRef<null | YMap>(null)
const pickupPoints = shallowRef(new Set<string>())
const localLocationGuid = ref(from.value.guid)
const localPickupPointGuid = ref('')
const locationValue = ref<YMapLocationRequest>({
  center: defaultCoordinates, // starting position [lng, lat]
  zoom: defaultZoom, // starting zoom
})

const newBounds = shallowRef<LngLatBounds>()

const title = computed(() => {
  return !!props.region?.id
    ? `Тариф на доставку груза до ПВЗ Яндекса в ${props.region?.prepositional || props.region?.name + ' ' + props.region?.type}`
    : 'Тариф на доставку груза по области до ПВЗ Яндекса'
})

const pickupPointsMarkers = computed(() => {
  const result: PickupPoint[] = []
  pickupPoints.value.forEach(p => {
    const pup = JSON.parse(p) as any
    result.push({
      ...pup,
      coordinates: [pup.coords.lon, pup.coords.lat],
      markerIcon: pup.guid === localPickupPointGuid.value
        ? '/svg/vz-geolocation-yandex-red.svg'
        : '/svg/vz-geolocation-yandex-grey.svg',
    })
  })

  return result
})

const orderRoute = computed((): RouteLocationRaw => {
  const query: any = {
    location_destination: localLocationGuid.value || from.value.guid,
    destination_point: 'pickupPoint',
    destination_pickup_point: localPickupPointGuid.value
  }

  return {
    name: 'order-create',
    query
  }
})

async function onBoundsChange(bounds?: LngLatBounds) {
  const startLength = pickupPoints.value.size
  await nextTick()
  if (bounds) {
     getPickupPointByBounds(bounds, 10000).then(list => {
       (list.data || []).forEach(pup => {
         pickupPoints.value.add(JSON.stringify(pup))
       })
       if (startLength !== pickupPoints.value.size) {
         triggerRef(pickupPoints)
       }
    })
  }
}

async function setPickupPoint(guid: string, center = false) {
  if (!guid) {
    return
  }

  localPickupPointGuid.value = guid
  const target = await getPickupPoint(guid)
  localLocationGuid.value = target.locationGuid

  if (center) {
    setCenter({ ...target.coords, zoom: 15 })
  }
}

function setCenter(options: { lat?: number|string, lon?: number|string, coords?: LngLat, zoom?: number|string|null }) {
  let { lat, lon, coords, zoom } = deepCopyObject(options)
  if (!coords) {
    coords = [+(lon || 0), +(lat || 0)]
  }
  if (zoom) {
    locationValue.value = {
      center: coords,
      zoom: +zoom
    }
  } else {
    locationValue.value = { center: coords }
  }
}

async function initSettings() {
  await nextTick()
  return onBoundsChange(map.value?.bounds)
}

const debouncedBoundsChange = useDebounceFn((bounds: LngLatBounds) => {
  onBoundsChange(bounds)
}, 1000)

const onMapUpdate: MapEventUpdateHandler = (e) => {
  if (e.location.bounds) {
    newBounds.value = e.location.bounds
  }
}

async function onLocationChange(value: string, obj: Location) {
  // Дожидаемся смены города
  await nextTick()
  await nextTick()
  if (localPickupPointGuid.value) {
    const pickupPoint = await getPickupPoint(localPickupPointGuid.value)
    if (pickupPoint?.locationGuid !== localLocationGuid.value) {
      localPickupPointGuid.value = ''
    }
  }

  setCenter({ coords: obj.coordinates as any, zoom: obj.zoom })
}

watch(newBounds, (newValue, oldValue) => {
  if (newValue && newValue?.toString() !== oldValue?.toString()) {
    debouncedBoundsChange(newValue)
  }
})

whenever(map, initSettings)
</script>

<style lang="scss" scoped>
.locations-pickup-points {
  margin-top: 90px;
  margin-bottom: 90px;

  h3 {
    font-size: 26px;
    line-height: 34px;
    font-weight: 500;
  }

  &-map {
    position: relative;

    @media screen and (max-width: $pub-xs) {
      display: flex;
      flex-direction: column-reverse;
    }

    .public-container {
      position: relative;
    }

    &-card {
      margin-top: 100px;
      position: absolute;
      width: 406px;
      height: 400px;
      right: 0;
      background-color: $white;
      padding: 30px;
      border-radius: 4px;
      z-index: 2;

      @media screen and (max-width: $pub-sm) {
        width: 363px;
      }

      @media screen and (max-width: $pub-xs) {
        position: relative;
        margin-top: 0;
        width: 100%;
      }

      &-title {
        font-size: 22px;
        line-height: 30px;
        font-weight: 500;
        margin-bottom: 30px;
      }

      .vz-button {
        width: 100%;
        justify-content: center;
      }
    }
  }

  .vz-yandex-map {
    width: 100%;
    height: 600px;
    background-color: $box-gray;

    :deep(.marker-icon-default) {
      width: 28px;
      height: 40px;
    }

    :deep(.ymaps3x0--main-engine-container) {
      filter: grayscale(1) !important;
    }

    :deep(canvas) {
      filter: grayscale(1) !important;
    }
  }
}
</style>
